import React, { useEffect, useState } from "react";
import { X, FileIcon, Server, Hash, Magnet as MagnetIcon, Loader2, Copy, Info, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import NfoViewerModal from "@/components/NfoViewerModal";
import TorrentFilesTree from "@/components/TorrentFilesTree";

function fmt(num) {
  if (!num) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let s = Number(num); let i = 0;
  while (s >= 1024 && i < units.length - 1) { s /= 1024; i++; }
  return `${s.toFixed(2)} ${units[i]}`;
}

// Release-name tag dictionary — order matters for nicer rendering.
const RELEASE_TAGS = [
  ["REMUX",        "#FFD700"], ["REPACK",     "#FF6B35"], ["PROPER",       "#FF6B35"],
  ["REAL",         "#FF6B35"], ["INTERNAL",   "#9CA3AF"], ["LIMITED",      "#9CA3AF"],
  ["EXTENDED",     "#A855F7"], ["DIRECTORS",  "#A855F7"], ["UNRATED",      "#A855F7"],
  ["IMAX",         "#22C77A"], ["REMASTERED", "#22C77A"], ["CRITERION",    "#22C77A"],
  ["DV",           "#7C3AED"], ["HDR10\\+",   "#FB7185"], ["HDR10",        "#FB7185"],
  ["HDR",          "#FB7185"], ["ATMOS",      "#06B6D4"], ["DTS-HD",       "#06B6D4"],
  ["TRUEHD",       "#06B6D4"], ["MULTI",      "#1CE783"], ["DUAL",         "#1CE783"],
];

function detectTags(name = "") {
  const upper = name.toUpperCase();
  const found = new Set();
  for (const [label] of RELEASE_TAGS) {
    const re = new RegExp(`(?:^|[\\W_])${label}(?:[\\W_]|$)`, "i");
    if (re.test(upper)) {
      const display = label.replace(/\\/g, "");
      found.add(display);
    }
  }
  // Extract trailing release-group (e.g. "-YAWNTiC" / "-RARBG")
  const grp = name.match(/-([A-Za-z0-9]{3,12})(?:\.[a-z0-9]{2,4})?$/);
  return { tags: [...found], group: grp ? grp[1] : null };
}

export default function TorrentInfoModal({ row, mediaType, tmdbId, onClose }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [title, setTitle] = useState(null);
  const [nfoOpen, setNfoOpen] = useState(false);
  const [srcInfo, setSrcInfo] = useState(null);

  useEffect(() => {
    if (!row) return;
    setMeta(null); setErr(null);
    if (!row.info_hash) {
      setErr("This torrent doesn't expose an info hash, so file details are unavailable.");
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/torrents/info/${row.info_hash}`);
        setMeta(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [row]);

  useEffect(() => {
    if (!tmdbId || !mediaType) { setTitle(null); return; }
    let cancel = false;
    api.get(`/details/${mediaType}/${tmdbId}`)
      .then(({ data }) => { if (!cancel) setTitle(data); })
      .catch(() => { if (!cancel) setTitle(null); });
    return () => { cancel = true; };
  }, [tmdbId, mediaType]);

  // Source-side rich description (YTS supplies description_full + screenshots + runtime).
  useEffect(() => {
    setSrcInfo(null);
    const imdb = title?.imdb_id;
    if (!row?.info_hash && !imdb) return;
    let cancel = false;
    const params = {};
    if (row?.info_hash) params.info_hash = row.info_hash;
    if (imdb) params.imdb_id = imdb;
    api.get("/torrents/source-info", { params })
      .then(({ data }) => { if (!cancel) setSrcInfo(data); })
      .catch(() => { if (!cancel) setSrcInfo(null); });
    return () => { cancel = true; };
  }, [row, title?.imdb_id]);

  if (!row) return null;

  const copyHash = async () => {
    try { await navigator.clipboard.writeText(row.info_hash); toast.success("Hash copied"); }
    catch { toast.error("Copy failed"); }
  };
  const copyMagnet = async () => {
    if (!row.magnet) return toast.error("Magnet unavailable");
    try { await navigator.clipboard.writeText(row.magnet); toast.success("Magnet copied"); }
    catch { toast.error("Copy failed"); }
  };

  const trackers = meta?.trackers?.length ? meta.trackers : [];

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="torrent-info-modal"
        className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="torrent-info-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black border border-[var(--tt-border)] flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-5 pb-3 border-b border-[var(--tt-border)]">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-semibold mb-1">
            <span className="px-2 py-0.5 rounded font-bold" style={{ background: "var(--tt-primary)", color: "white" }}>{row.source}</span>
            <span>{row.quality}</span>
            {row.codec && row.codec !== "Other" && <span>· {row.codec}</span>}
            {row.language && <span>· {row.language}</span>}
          </div>
          <h2 className="font-display text-lg font-bold break-words pr-8">{row.name}</h2>
          <div className="flex items-center gap-3 mt-2 text-xs text-[var(--tt-text-muted)] font-mono-tt">
            <span>↑ {row.seeders}</span>
            <span>↓ {row.leechers}</span>
            <span>{row.size}</span>
          </div>
        </div>

        <div className="overflow-y-auto">
          {row.info_hash && (
            <div className="px-6 py-3 flex items-center gap-3 border-b border-[var(--tt-border)] text-xs">
              <Hash className="w-3.5 h-3.5 text-[var(--tt-text-muted)] shrink-0" />
              <code className="font-mono-tt text-[11px] truncate flex-1" data-testid="torrent-info-hash">{row.info_hash}</code>
              <button onClick={copyHash} className="p-1.5 rounded hover:bg-white/10" title="Copy hash">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Decoded release-name tag pills */}
          {(() => {
            const { tags, group } = detectTags(row.name);
            if (!tags.length && !group) return null;
            return (
              <div className="px-6 py-3 border-b border-[var(--tt-border)] flex items-center gap-1.5 flex-wrap" data-testid="torrent-info-release-tags">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-muted)] mr-1">Release</span>
                {tags.map((t) => {
                  const meta = RELEASE_TAGS.find(([l]) => l.replace(/\\/g, "") === t);
                  const color = meta ? meta[1] : "#9CA3AF";
                  return (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border"
                      style={{ color, borderColor: `${color}55`, background: `${color}14` }}
                    >
                      {t}
                    </span>
                  );
                })}
                {group && (
                  <span
                    className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ml-1 font-mono-tt"
                    style={{ color: "var(--tt-primary)", borderColor: "rgba(255,42,109,0.4)", background: "rgba(255,42,109,0.08)" }}
                    title="Release group"
                  >
                    -{group}
                  </span>
                )}
              </div>
            );
          })()}

          {/* About this title — TMDB synopsis */}
          {title && (title.overview || title.poster) && (
            <section className="px-6 py-4 border-b border-[var(--tt-border)] flex gap-3" data-testid="torrent-info-about">
              {title.poster && (
                <img
                  src={title.poster}
                  alt={title.title}
                  className="w-16 h-24 object-cover rounded-md flex-shrink-0 border border-[var(--tt-border)]"
                  loading="lazy"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Info className="w-3 h-3 text-[var(--tt-primary)]" />
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-primary)]">About this title</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <h4 className="font-display font-bold text-sm truncate" title={title.title}>{title.title}</h4>
                  {title.year && (
                    <span className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt">{title.year}</span>
                  )}
                  {!!title.rating && (
                    <span className="text-[11px] flex items-center gap-0.5 text-[var(--tt-yellow)] font-bold">
                      <Star className="w-3 h-3 fill-current" /> {Number(title.rating).toFixed(1)}
                    </span>
                  )}
                </div>
                {!!title.genres?.length && (
                  <div className="text-[10px] text-[var(--tt-text-muted)] mb-1.5">
                    {title.genres.slice(0, 4).join(" · ")}
                  </div>
                )}
                {title.overview && (
                  <p className="text-xs text-white/75 leading-snug line-clamp-4">{title.overview}</p>
                )}
              </div>
            </section>
          )}

          {/* Source-side description (currently YTS) */}
          {srcInfo && (srcInfo.description_full || srcInfo.summary || srcInfo.screenshots?.length) && (
            <section className="px-6 py-4 border-b border-[var(--tt-border)]" data-testid="torrent-info-source-description">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold"
                      style={{ color: "#FFD700" }}>
                  Source · {srcInfo.source}
                </span>
                {srcInfo.runtime ? (
                  <span className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt">{srcInfo.runtime} min</span>
                ) : null}
                {srcInfo.language ? (
                  <span className="text-[10px] uppercase font-bold bg-white/5 text-white/60 px-1.5 py-0.5 rounded">
                    {srcInfo.language}
                  </span>
                ) : null}
                {srcInfo.mpa_rating ? (
                  <span className="text-[10px] uppercase font-bold bg-white/5 text-white/60 px-1.5 py-0.5 rounded">
                    {srcInfo.mpa_rating}
                  </span>
                ) : null}
              </div>
              {(srcInfo.description_full || srcInfo.summary) && (
                <p className="text-xs text-white/80 leading-relaxed whitespace-pre-line line-clamp-[10]">
                  {srcInfo.description_full || srcInfo.summary}
                </p>
              )}
              {!!srcInfo.screenshots?.length && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {srcInfo.screenshots.map((s, i) => (
                    <a key={i} href={s} target="_blank" rel="noreferrer noopener" data-testid={`torrent-info-screenshot-${i}`}>
                      <img
                        src={s}
                        alt={`Screenshot ${i + 1}`}
                        loading="lazy"
                        className="w-full h-20 object-cover rounded-md border border-[var(--tt-border)] hover:border-[var(--tt-primary)]/50 transition-colors"
                      />
                    </a>
                  ))}
                </div>
              )}
            </section>
          )}

          {loading ? (
            <div className="flex items-center justify-center gap-2 text-[var(--tt-text-muted)] py-12 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Fetching torrent metadata from public caches…
            </div>
          ) : err ? (
            <div className="m-6 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-3" data-testid="torrent-info-error">
              {err}
            </div>
          ) : meta ? (
            <>
              <div className="px-6 py-3 border-b border-[var(--tt-border)] grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase text-[var(--tt-text-faint)] font-semibold">Files</div>
                  <div className="font-display text-xl font-bold">{meta.files.length}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[var(--tt-text-faint)] font-semibold">Total Size</div>
                  <div className="font-display text-xl font-bold">{fmt(meta.total_size)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[var(--tt-text-faint)] font-semibold">Piece Size</div>
                  <div className="font-display text-xl font-bold">{fmt(meta.piece_length)}</div>
                </div>
              </div>

              <section className="px-6 py-4 border-b border-[var(--tt-border)]">
                <div className="flex items-center gap-2 mb-3">
                  <FileIcon className="w-4 h-4 text-[var(--tt-primary)]" />
                  <h3 className="font-display font-bold text-sm">Files</h3>
                  <span className="bg-[var(--tt-surface-2)] text-[10px] font-mono-tt px-2 py-0.5 rounded">{meta.files.length}</span>
                </div>
                <TorrentFilesTree files={meta.files} onViewNfo={() => setNfoOpen(true)} />
              </section>

              <section className="px-6 py-4 border-b border-[var(--tt-border)]">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-4 h-4 text-[var(--tt-purple)]" />
                  <h3 className="font-display font-bold text-sm">Trackers</h3>
                  <span className="bg-[var(--tt-surface-2)] text-[10px] font-mono-tt px-2 py-0.5 rounded">{trackers.length}</span>
                </div>
                <ul className="space-y-1 max-h-48 overflow-y-auto" data-testid="torrent-info-trackers">
                  {trackers.length === 0 && <li className="text-xs text-[var(--tt-text-muted)]">No trackers in this torrent.</li>}
                  {trackers.map((t, i) => (
                    <li key={i} className="text-[11px] font-mono-tt text-[var(--tt-text-muted)] px-2 py-1 rounded hover:bg-white/[0.03] truncate" title={t}>
                      {t}
                    </li>
                  ))}
                </ul>
              </section>

              {(meta.created_by || meta.comment) && (
                <section className="px-6 py-3 text-xs text-[var(--tt-text-muted)] space-y-1">
                  {meta.created_by && <div>Created by: <span className="text-zinc-300">{meta.created_by}</span></div>}
                  {meta.comment && <div>Comment: <span className="text-zinc-300">{meta.comment}</span></div>}
                  {meta.private && <div className="text-amber-400">Private tracker</div>}
                </section>
              )}
            </>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-[var(--tt-border)] flex items-center gap-2">
          <button
            onClick={copyMagnet}
            data-testid="torrent-info-copy-magnet"
            className="flex items-center gap-2 bg-[var(--tt-surface-2)] hover:bg-[#222] text-xs font-semibold px-3 py-1.5 rounded-full border border-[var(--tt-border)]"
          >
            <Copy className="w-3.5 h-3.5" /> Copy magnet
          </button>
          <a
            href={row.magnet || "#"}
            onClick={(e) => { if (!row.magnet) { e.preventDefault(); toast.error("Magnet unavailable"); } }}
            data-testid="torrent-info-open-magnet"
            className="ml-auto flex items-center gap-2 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-semibold px-4 py-1.5 rounded-full"
          >
            <MagnetIcon className="w-3.5 h-3.5" />
            Open magnet
          </a>
        </div>
      </div>

      {nfoOpen && (
        <NfoViewerModal
          releaseName={row.name}
          onClose={() => setNfoOpen(false)}
        />
      )}
    </div>
  );
}
